/**
 * A scope's dimension list: one collapsible section per dimension, each opening onto its own
 * value tree (`ValueTree.tsx`). `profileScope` narrows what a dimension's extension switches
 * offer -- Perfis never gets `auto`, since a new profile value always waits for an
 * administrator (the Interfaces contract draws no such flag; this is drawn straight from the
 * brief's own rule, not inferred from a field).
 */
import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { ChevronDown, Ellipsis, Plus } from 'lucide-react';

import { ConfirmDialog } from '../../../chrome/form';
import { FormDialog } from '../../../chrome/form/FormDialog';
import { fieldErrorOf } from '../../../chrome/form/fieldErrorOf';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card, CardContent } from '../../../ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { Field, FieldError, FieldLabel } from '../../../ui/field';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Switch } from '../../../ui/switch';
import { useCreateDimension, useDeleteDimension, useUpdateDimension } from './api/queries';
import type { ExtensionMode, OntologyDimension, OntologyScope } from './api/schemas';
import { om } from './messages';
import { ValueTree } from './ValueTree';

function NewDimensionDialog(props: {
  tenantId: string;
  scope: OntologyScope;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): ReactNode {
  const { open, onOpenChange } = props;
  const createMutation = useCreateDimension(props.tenantId, props.scope);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const nameId = useId();

  function handleOpenChange(next: boolean): void {
    if (next) {
      setName('');
      setError(null);
    }
    onOpenChange(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void | Promise<unknown> {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed === '') {
      setError(om('dimensions.new.name.required'));
      return;
    }
    return createMutation.mutateAsync({ name: trimmed });
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={om('dimensions.new.title')}
      submitLabel={om('dimensions.new')}
      pending={createMutation.isPending}
      failure={createMutation.error?.failure ?? null}
      onSubmit={handleSubmit}
    >
      <Field data-invalid={error !== null || undefined}>
        <FieldLabel htmlFor={nameId}>{om('dimensions.new.name.label')}</FieldLabel>
        <Input id={nameId} value={name} onChange={(event) => setName(event.target.value)} />
        <FieldError>
          {error ?? fieldErrorOf(createMutation.error?.failure ?? null, 'name')}
        </FieldError>
      </Field>
    </FormDialog>
  );
}

function RenameDimensionDialog(props: {
  tenantId: string;
  scope: OntologyScope;
  dimension: OntologyDimension;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): ReactNode {
  const { dimension, open, onOpenChange } = props;
  const updateMutation = useUpdateDimension(props.tenantId, props.scope);
  const [name, setName] = useState(dimension.name);
  const [error, setError] = useState<string | null>(null);
  const nameId = useId();

  function handleOpenChange(next: boolean): void {
    if (next) {
      setName(dimension.name);
      setError(null);
    }
    onOpenChange(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void | Promise<unknown> {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed === '') {
      setError(om('dimensions.new.name.required'));
      return;
    }
    return updateMutation.mutateAsync({ dimensionId: dimension.id, body: { name: trimmed } });
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={om('dimensions.rename.title')}
      submitLabel={om('flags.save')}
      pending={updateMutation.isPending}
      failure={updateMutation.error?.failure ?? null}
      onSubmit={handleSubmit}
    >
      <Field data-invalid={error !== null || undefined}>
        <FieldLabel htmlFor={nameId}>{om('dimensions.new.name.label')}</FieldLabel>
        <Input id={nameId} value={name} onChange={(event) => setName(event.target.value)} />
        <FieldError>
          {error ?? fieldErrorOf(updateMutation.error?.failure ?? null, 'name')}
        </FieldError>
      </Field>
    </FormDialog>
  );
}

function DimensionSection(props: {
  tenantId: string;
  scope: OntologyScope;
  dimension: OntologyDimension;
  profileScope: boolean;
}): ReactNode {
  const { tenantId, scope, dimension, profileScope } = props;
  const [open, setOpen] = useState(true);
  const [dialog, setDialog] = useState<'rename' | 'delete' | null>(null);
  const switchId = useId();
  const updateMutation = useUpdateDimension(tenantId, scope);
  const deleteMutation = useDeleteDimension(tenantId, scope);

  function setExtension(value: ExtensionMode): void {
    updateMutation.mutate({ dimensionId: dimension.id, body: { extension: value } });
  }

  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CardContent className="flex flex-col gap-4">
          {/* Below lg the switches take their own line under the title, keeping the menu on
              the header row; from lg they sit inline before it. A pair is 8px apart, pairs and
              the menu 32px (the row's 12px gap plus the menu's own 20px). */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <CollapsibleTrigger asChild>
              <button type="button" className="flex items-center gap-2 text-left">
                <ChevronDown
                  aria-hidden
                  className={
                    open ? 'size-4 transition-transform' : 'size-4 -rotate-90 transition-transform'
                  }
                />
                <span className="text-title-xs text-foreground">{dimension.name}</span>
              </button>
            </CollapsibleTrigger>
            {dimension.sensitive && (
              <Badge variant="secondary">{om('dimensions.sensitive.badge')}</Badge>
            )}

            <div className="order-last flex basis-full flex-wrap items-center gap-x-8 gap-y-2 lg:order-none lg:ml-auto lg:basis-auto">
              <div className="flex items-center gap-2">
                <Label
                  htmlFor={`${switchId}-extract`}
                  className="text-body-sm font-normal text-muted-foreground"
                >
                  {om('dimensions.extract.label')}
                </Label>
                <Switch
                  id={`${switchId}-extract`}
                  checked={dimension.extract}
                  onCheckedChange={(checked) =>
                    updateMutation.mutate({ dimensionId: dimension.id, body: { extract: checked } })
                  }
                />
              </div>

              <div className="flex items-center gap-2">
                <Label
                  htmlFor={`${switchId}-suggest`}
                  className="text-body-sm font-normal text-muted-foreground"
                >
                  {om('dimensions.extension.suggest')}
                </Label>
                <Switch
                  id={`${switchId}-suggest`}
                  checked={dimension.extension !== 'off'}
                  onCheckedChange={(checked) => setExtension(checked ? 'suggest' : 'off')}
                />
              </div>

              {!profileScope && (
                <div
                  className="group flex items-center gap-2"
                  data-disabled={dimension.extension === 'off'}
                >
                  <Label
                    htmlFor={`${switchId}-auto`}
                    className="text-body-sm font-normal text-muted-foreground"
                  >
                    {om('dimensions.extension.auto')}
                  </Label>
                  <Switch
                    id={`${switchId}-auto`}
                    checked={dimension.extension === 'auto'}
                    disabled={dimension.extension === 'off'}
                    onCheckedChange={(checked) => setExtension(checked ? 'auto' : 'suggest')}
                  />
                </div>
              )}
            </div>

            <div className="ml-auto lg:ml-5">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={om('dimensions.actions.rename')}
                  >
                    <Ellipsis aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => setDialog('rename')}>
                    {om('dimensions.actions.rename')}
                  </DropdownMenuItem>
                  <DropdownMenuItem variant="destructive" onSelect={() => setDialog('delete')}>
                    {om('dimensions.actions.delete')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {profileScope && (
            <p className="text-caption text-muted-foreground">
              {om('dimensions.extension.profileHint')}
            </p>
          )}

          <CollapsibleContent>
            <ValueTree
              dimensionId={dimension.id}
              values={dimension.values}
              tenantId={tenantId}
              scope={scope}
            />
          </CollapsibleContent>
        </CardContent>
      </Collapsible>

      <RenameDimensionDialog
        tenantId={tenantId}
        scope={scope}
        dimension={dimension}
        open={dialog === 'rename'}
        onOpenChange={(next) => setDialog(next ? 'rename' : null)}
      />
      <ConfirmDialog
        open={dialog === 'delete'}
        onOpenChange={(next) => {
          setDialog(next ? 'delete' : null);
          if (!next) deleteMutation.reset();
        }}
        title={om('dimensions.delete.title', { name: dimension.name })}
        description={om('dimensions.delete.description')}
        confirmLabel={om('dimensions.delete.confirm')}
        destructive
        pending={deleteMutation.isPending}
        failure={deleteMutation.error?.failure ?? null}
        onConfirm={() => deleteMutation.mutateAsync(dimension.id)}
      />
    </Card>
  );
}

export function DimensionList(props: {
  tenantId: string;
  scope: OntologyScope;
  dimensions: OntologyDimension[];
  profileScope: boolean;
}): ReactNode {
  const { tenantId, scope, dimensions, profileScope } = props;
  const [creating, setCreating] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-title-sm text-foreground">{om('dimensions.heading')}</h2>
        <Button type="button" variant="outline" size="sm" onClick={() => setCreating(true)}>
          <Plus aria-hidden />
          {om('dimensions.new')}
        </Button>
      </div>

      {[...dimensions]
        .sort((a, b) => a.position - b.position)
        .map((dimension) => (
          <DimensionSection
            key={dimension.id}
            tenantId={tenantId}
            scope={scope}
            dimension={dimension}
            profileScope={profileScope}
          />
        ))}

      <NewDimensionDialog
        tenantId={tenantId}
        scope={scope}
        open={creating}
        onOpenChange={setCreating}
      />
    </div>
  );
}
